import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import {
  IssueClassifySurface,
  IssueReportSource,
} from '../../redux/features/issueReport/issueReport.props';
import { selectIsIssueReportingEnabled } from '../../redux/features/config/config.selector';
import {
  selectActiveFlagWindowExpiresAt,
  selectActiveIssueReport,
  selectFlagWindowRemainingMs,
  selectIssueReports,
  selectOutstandingIssueReports,
} from '../../redux/features/issueReport/issueReport.selectors';
import {
  closeFlagWindow,
  commitIssueType,
  flagIssue,
  pauseFlagWindow,
  resumeFlagWindow,
  voidIssue,
} from '../../redux/features/issueReport/issueReport.slice';

/**
 * HITL-1492 Pillar 3, Part A — the single seam the issue-reporting UI consumes.
 *
 * The flag gate lives here, ONCE, so no caller can leak a network call with the
 * feature off. Do NOT re-AND `isEnabled` at a call site.
 */
const useIssueReport = () => {
  const dispatch = useAppDispatch();

  const isEnabled = useSelector(selectIsIssueReportingEnabled);

  const reports = useSelector(selectIssueReports);
  const activeReport = useSelector(selectActiveIssueReport);
  const activeWindowExpiresAt = useSelector(selectActiveFlagWindowExpiresAt);
  const remainingMs = useSelector(selectFlagWindowRemainingMs);
  /**
   * Still needing the agent, newest first, SHIFT-scoped — what the badge counts and
   * what every backlog surface lists. Reads no session state, which is why it still
   * returns rows on the task-router lobby.
   */
  const outstandingReports = useSelector(selectOutstandingIssueReports);

  /** Returns the new `issue_id`, so HITL-1499's toast can address its own record. */
  const flag = useCallback(
    (
      args: {
        source?: IssueReportSource;
        /** Set when the gesture itself classifies — see the transcript entries. */
        issue_type?: string | null;
        issue_detail?: string | null;
      } = {}
    ) => {
      if (!isEnabled) return null;
      return dispatch(flagIssue(args));
    },
    [isEnabled, dispatch]
  );

  const commit = useCallback(
    (args: {
      issue_id: string;
      issue_type: string | null;
      issue_detail?: string | null;
      /** The surface the agent classified from — null when nothing was classified. */
      classified_from?: IssueClassifySurface | null;
    }) => {
      if (!isEnabled) return null;
      return dispatch(commitIssueType(args));
    },
    [isEnabled, dispatch]
  );

  const voidReport = useCallback(
    (issueId: string) => {
      if (!isEnabled) return null;
      return dispatch(voidIssue({ issue_id: issueId }));
    },
    [isEnabled, dispatch]
  );

  /** Detail field gained focus — stop the clock. */
  const pauseWindow = useCallback(() => {
    if (!isEnabled) return;
    dispatch(pauseFlagWindow());
  }, [isEnabled, dispatch]);

  /** Detail field lost focus — resume from the banked remainder. */
  const resumeWindow = useCallback(() => {
    if (!isEnabled) return;
    dispatch(resumeFlagWindow());
  }, [isEnabled, dispatch]);

  const closeWindow = useCallback(() => {
    if (!isEnabled) return;
    dispatch(closeFlagWindow());
  }, [isEnabled, dispatch]);

  return {
    isEnabled,
    reports,
    activeReport,
    activeWindowExpiresAt,
    remainingMs,
    outstandingReports,
    flagIssue: flag,
    commitIssueType: commit,
    voidIssue: voidReport,
    pauseFlagWindow: pauseWindow,
    resumeFlagWindow: resumeWindow,
    closeFlagWindow: closeWindow,
  };
};

export default useIssueReport;
